import { fmtDuration } from '@/shared/hooks/useTicker';
import './live.css';
import { CountUp } from '@/components/ui/CountUp';

/**
 * אריחי הניטור החי לפי GoCall v2: לוח ההמתנה (5 אריחים עם מספר גדול צבוע),
 * ספירת נציגים לפי סטטוס (אריחים קטנים עם נקודת צבע) וכרטיסי התורים.
 * תצוגה בלבד — הנתונים מגיעים מהסנאפשוט של LiveMonitor.
 */

export interface WallboardData {
  waiting_now: number;
  longest_wait_sec: number;
  avg_wait_sec: number;
  agents_available: number;
  agents_oncall: number;
}

export interface QueueData {
  id: number;
  name: string;
  ext?: string;
  sla_sec: number;
  calls_today: number;
  answered_today: number;
  sla_pct: number | null;
  waiting_now: number;
}

const tileStyle: React.CSSProperties = {
  padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0,
};

/** השהיית כניסה לאריח (משתנה --d של .card). */
const delay = (s: number) => ({ '--d': `${s}s` }) as React.CSSProperties;

function BigTile({ label, value, color, live, index }: { label: string; value: string | number; color?: string; live?: boolean; index: number }) {
  return (
    // .card נכנס ב-gcUp; hover-lift מרים 3px — כמו בעיצוב (השהיה 0.1+i*0.05)
    <div className="card hover-lift" style={{ ...tileStyle, ...delay(0.1 + index * 0.05) }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-2)' }}>
        {live && <span className="anim-blink" style={{ width: 6, height: 6, borderRadius: 'var(--r-pill)', background: color, animationDuration: '1.4s' }} />}
        {label}
      </span>
      <span style={{ fontSize: 30, fontWeight: 600, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', color: color ?? 'var(--text)' }}>
        {typeof value === 'number' ? <CountUp value={value} /> : value}
      </span>
    </div>
  );
}

export function WallboardTiles({ wb }: { wb: WallboardData }) {
  // המתנה ארוכה מ-90 שניות נצבעת באדום — כמו בעיצוב.
  const longColor = wb.longest_wait_sec > 90 ? 'var(--danger)' : undefined;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
      <BigTile index={0} label="ממתינים כעת" value={wb.waiting_now} color={wb.waiting_now ? 'var(--warn)' : undefined} live={wb.waiting_now > 0} />
      <BigTile index={1} label="המתנה ארוכה ביותר" value={fmtDuration(wb.longest_wait_sec)} color={longColor} live={!!longColor} />
      <BigTile index={2} label="המתנה ממוצעת" value={fmtDuration(wb.avg_wait_sec)} />
      <BigTile index={3} label="נציגים זמינים" value={wb.agents_available} color="var(--ok)" />
      <BigTile index={4} label="נציגים בשיחה" value={wb.agents_oncall} color="var(--accent)" />
    </div>
  );
}

/** צבע הנקודה לכל סטטוס נוכחות. */
export const STATUS_DOT: Record<string, string> = {
  connected: 'var(--ok)',
  ringing: 'var(--warn)',
  paperwork: 'var(--busy)',
  available: 'var(--ok)',
  oncall: 'var(--accent)',
  acw: 'var(--busy)',
  break: 'var(--warn)',
  lunch: 'var(--warn)',
  training: 'var(--faint)',
  offline: 'var(--danger)',
};

export function StatusTiles({ totals, labelFor }: { totals: Record<string, number>; labelFor: (k: string) => string }) {
  const entries = Object.entries(totals);
  if (entries.length === 0) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))', gap: 10 }}>
      {entries.map(([k, v], i) => (
        <div key={k} className="card lm-status-tile" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6, borderRadius: 'var(--r-3)', ...delay(0.15 + i * 0.04) }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-2)' }}>
            <span style={{ width: 8, height: 8, borderRadius: 'var(--r-pill)', background: STATUS_DOT[k] ?? 'var(--faint)' }} />
            {labelFor(k)}
          </span>
          <span style={{ fontSize: 26, fontWeight: 600, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}><CountUp value={v} /></span>
        </div>
      ))}
    </div>
  );
}

export function QueueCards({ queues }: { queues: QueueData[] }) {
  return (
    <div className="card" style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <span style={{ fontWeight: 600, fontSize: 15 }}>תורים</span>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        {queues.map((q, i) => {
          const waiting = q.waiting_now > 0;
          const w = q.calls_today ? Math.round((q.answered_today / q.calls_today) * 100) : 0;
          const slaColor = q.sla_pct == null ? 'var(--muted)' : q.sla_pct >= 80 ? 'var(--ok)' : q.sla_pct >= 50 ? 'var(--warn)' : 'var(--danger)';
          return (
            <div key={q.id} className="lm-queue" style={{
              display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 16px', borderRadius: 12, border: '1px solid var(--glass-line)', background: 'var(--glass-soft)',
              animation: `gcUp .5s var(--ease-out) ${0.1 + i * 0.07}s backwards`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, minWidth: 0 }}>
                  {q.name}
                  {q.ext && <span dir="ltr" style={{ fontSize: 'var(--t-xs)', fontWeight: 500, padding: '1px 8px', borderRadius: 'var(--r-pill)', background: 'var(--surface-3)', color: 'var(--text-2)' }}>ext {q.ext}</span>}
                </span>
                <span className={waiting ? 'anim-blink' : undefined} style={{
                  fontSize: 12, fontWeight: 600, padding: '2px 9px', borderRadius: 'var(--r-pill)',
                  background: waiting ? 'var(--warn-soft)' : 'var(--ok-soft)', color: waiting ? 'var(--warn)' : 'var(--ok)',
                }}>
                  {waiting ? `${q.waiting_now} ממתינים` : 'אין ממתינים'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12.5, color: 'var(--text-2)' }}>
                <span><span dir="ltr" style={{ fontVariantNumeric: 'tabular-nums' }}>{q.answered_today}/{q.calls_today}</span> נענו היום</span>
                <span>
                  SLA {q.sla_sec} שנ׳
                  {q.sla_pct != null && <span style={{ color: slaColor, fontWeight: 600 }}> · {q.sla_pct}%</span>}
                </span>
              </div>
              <div style={{ height: 6, borderRadius: 'var(--r-pill)', background: 'var(--surface-3)' }}>
                {/* הפס גדל פעם אחת ב-mount (anim-grow); עדכון חי רק מזיז את הרוחב */}
                <div className="anim-grow" style={{ height: '100%', width: `${w}%`, borderRadius: 'var(--r-pill)', background: 'var(--accent)', transition: 'width .6s var(--ease-out)' }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
